import { z } from 'zod'

import {
  getEntityRegistry,
  isEntityEnabled,
} from '../../engine/entity-registry'
import { requireDataClassName } from '../shared/entity-serializer'
import { getOrBuildSchema } from '../shared/schema-cache'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'

const SECTION_LABELS: Record<string, string> = {
  activities: 'Activities',
  tickets: 'Tickets',
  opportunities: 'Deal pipeline',
  quotes: 'Deal pipeline',
  orders: 'Deal pipeline',
  persons: 'Persons',
  messages: 'Messages',
  followUps: 'Follow-up Activities',
}

export const getSchemaTool = defineTool({
  name: 'get_schema',
  // Page-session schema cache lives on SessionContext.schemas; tool-cache layer
  // would only confuse things. Schemas barely change — cache lifetime = page session.
  cacheable: false,
  description:
    'Get the schema (field names, types, enum values) and available relation sections for a CRM entity type. ' +
    'MUST be called before get_entity for any summary request — the result includes _instruction and _summarySections ' +
    'that tell you which show_options to present to the user before fetching data. ' +
    'READ side only: do NOT call this for a create or update — use get_write_schema for writes.',
  inputSchema: () =>
    z.object({
      dataClass: z
        .string()
        .describe(
          'Entity type, e.g. Company, Person, Ticket, Opportunity, Order, Quote, Activity, Lead, Timerecord',
        ),
      entityId: z
        .string()
        .optional()
        .describe(
          'Pass the entityId when summarizing a single known record. Omit when querying schema for list or filtering purposes.',
        ),
    }),

  async run(args, ctx) {
    const { dataClass: rawDataClass, entityId } = args
    const dataClass = requireDataClassName(rawDataClass)
    if (!dataClass) {
      return { error: `Unknown entity: ${rawDataClass}` }
    }

    const schema = await getOrBuildSchema(dataClass, ctx)
    if (!schema) return { error: `Unknown entity: ${dataClass}` }

    const meta = getEntityRegistry()[dataClass]
    const summarySections = meta
      ? [
          ...new Set(
            Object.entries(meta.relations)
              // Drop documents (never a summary section) and any relation whose
              // target entity is disabled on this instance — so a standalone
              // instance without Opportunity / Quote / Order / Ticket never
              // offers a Deal pipeline or Tickets section. Mirrors the
              // detail-page tab gate (filterTabsByFeatureFlags).
              .filter(
                ([key, rel]) =>
                  key !== 'documents' &&
                  isEntityEnabled(rel.dataClass, ctx.featureFlags),
              )
              .map(([key]) => SECTION_LABELS[key] ?? key),
          ),
        ]
      : []

    return {
      ...schema,
      // Entity-specific LLM hints — add notes to fetchHints.note in connector/registry.ts
      ...(schema.fetchHints?.note ? { _note: schema.fetchHints.note } : {}),
      ...(entityId && summarySections.length > 0
        ? {
            _summarySections: summarySections,
            _instruction:
              `For a broad summary request: call show_options with multiSelect=true and options=${JSON.stringify(summarySections)} BEFORE calling get_entity. ` +
              'Do not fetch any entity data until the user has selected sections.',
          }
        : {}),
    }
  },
})
